import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import {
  ContratoArquivoForm,
  ContratoArquivoSchema,
} from "../../../schemas/Contrato/contrato_arquivo";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import styled from "@emotion/styled";
import { ChangeEvent, useCallback, useEffect } from "react";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

type Props = {
  open: boolean;
  saving: boolean;
  selectedArquivo: ContratoArquivoForm | null;
  IdContrato: number;
  onSubmit: (form: ContratoArquivoForm) => void;
  hookForm: UseFormReturn<ContratoArquivoForm>;
  handleDownload: (idContratoArquivo: number, nomeArquivo: string) => void;
  handleArquivoDeleteOpenState: (
    acao: string,
    campo: string,
    estado: boolean,
  ) => void;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
  onClose: () => void;
};

export const DocumentosModal = ({
  open,
  saving,
  IdContrato,
  selectedArquivo,
  hookForm,
  onSubmit,
  onOpenChangeHistory,
  handleDownload,
  handleArquivoDeleteOpenState,
  onClose,
}: Props) => {

  useEffect(() => {
    if (open) {
      if (selectedArquivo) {

        hookForm.setValue("IdContrato", selectedArquivo.IdContrato);
        hookForm.setValue("IdContratoArquivo", selectedArquivo.IdContratoArquivo);
        hookForm.setValue("Arquivo", selectedArquivo.Arquivo);
        hookForm.setValue("DataVencimento", selectedArquivo.DataVencimento);
        hookForm.setValue("Descricao", selectedArquivo.Descricao);
        hookForm.setValue("Nome", selectedArquivo.Nome);
        hookForm.setValue("Obsoleto", Boolean(selectedArquivo.Obsoleto) === false ? 0 : 1);
        
      } else {
        hookForm.reset({
          IdContrato: IdContrato,
          IdContratoArquivo: 0,
          Arquivo: null,
          DataVencimento: null,
          Descricao: "",
          Nome: "",
          Obsoleto: 1,
        });
      }
    }
  }, [hookForm, open, selectedArquivo, IdContrato]);

  const handleChangeHistory = useCallback(() => {
    if (selectedArquivo) {
      onOpenChangeHistory(
        selectedArquivo.IdContratoArquivo,
        LogTipoTabela.CONTRATO_ARQUIVO,
      );
    }
  }, [selectedArquivo, onOpenChangeHistory]);

  const handleSubmit = useCallback(
    (form: ContratoArquivoForm) => {
      if (!saving) {
        onSubmit(form);
      }
    },
    [onSubmit, saving],
  );

  const handleClose = useCallback(() => {
    if (!saving) {
      onClose();
      hookForm.reset();
    }
  }, [hookForm, onClose, saving]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      slotProps={{
        paper: {
          component: "form",
          onSubmit: hookForm.handleSubmit(handleSubmit),
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "50rem",
          },
        },
      }}
    >
      <DialogTitle>Documentos Diversos</DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer
            title={selectedArquivo ? "Editar" : "Cadastrar"}
            arrangement="fullRow"
          >
            <div className="flex flex-col gap-4">
              <FormRadioGroupInput
                name="Obsoleto"
                label="Status do Arquivo"
                readOnly={saving}
                options={[
                  { id: 1, label: "Atual" },
                  { id: 0, label: "Obsoleto" },
                ]}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name="Descricao"
                label="Nome do Documento"
                required={true}
                reactHookForm={hookForm}
              />

              <FormDatePickerInput
                name="DataVencimento"
                label="Data Vencimento"
                reactHookForm={hookForm}
              />

              <div className="flex flex-col gap-2 p-4 mt-2 border border-gray-200 dark:border-gray-700 rounded bg-gray-50/50 dark:bg-transparent">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Anexar documento
                </span>

                <Controller
                  name="Arquivo"
                  control={hookForm.control}
                  render={({ field }) =>
                    selectedArquivo &&
                    selectedArquivo.Nome &&
                    selectedArquivo.Nome !== null ? (
                      <div className="w-full flex flex-row justify-between items-center bg-white dark:bg-gray-800 p-2 border border-gray-200 dark:border-gray-700 rounded">
                        <span
                          className="cursor-pointer text-sm font-medium hover:text-primary-green hover:underline"
                          onMouseDown={() =>
                            handleDownload(
                              selectedArquivo.IdContratoArquivo,
                              selectedArquivo.Nome!,
                            )
                          }
                        >
                          {selectedArquivo.Nome &&
                          selectedArquivo.Nome.length > 40
                            ? `${selectedArquivo.Nome.substring(0, 40)}...`
                            : selectedArquivo.Nome || "Arquivo 1"}
                        </span>
                        <IconButton
                          onClick={() =>
                            handleArquivoDeleteOpenState(
                              "limparArquivo1",
                              "Arquivo",
                              true,
                            )
                          }
                          size="small"
                        >
                          <DynamicIcon
                            nameIcon="DeleteForeverRounded"
                            size={22}
                            className="text-red-500"
                          />
                        </IconButton>
                      </div>
                    ) : (
                      <PrimaryButton
                        component="label"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                        disabled={saving}
                        startIcon={
                          <DynamicIcon
                            nameIcon="Download"
                            size={20}
                            className="text-white"
                          />
                        }
                      >
                        {field.value ? field.value.name : "Escolher arquivo"}
                        <VisuallyHiddenInput
                          type="file"
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              field.onChange(file);
                              hookForm.setValue("Nome", file.name, {
                                shouldDirty: true,
                              });
                              hookForm.setValue("limparArquivo1", false, {
                                shouldDirty: true,
                              });
                            }
                          }}
                          multiple={false}
                        />
                      </PrimaryButton>
                    )
                  }
                />
              </div>
            </div>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div className="w-[220px]">
            {isNotNullOrUndefined(selectedArquivo) && (
              <PrimaryButton buttonColor="yellow" onClick={handleChangeHistory}>
                <span className="text-xs">Historico de alterações</span>
              </PrimaryButton>
            )}
          </div>
          <div className="flex flex-row gap-2 w-full justify-end">
            <div
              className="p-2 hover:bg-gray-100 cursor-pointer dark:hover:bg-gray-800 rounded-[4px] font-semibold"
              onClick={handleClose}
            >
              <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton type="submit" buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};
